import { css } from "@emotion/css";
import { borderRadius, space } from "@octopusdeploy/design-system-tokens";
import type { ObjectPropertyPath } from "@octopusdeploy/type-utils";
import type React from "react";
import type { TooltipContentProps } from "recharts";
import type { NameType, Payload, ValueType } from "recharts/types/component/DefaultTooltipContent";
interface TypedPayload<TResource> extends Payload<ValueType, NameType> {
    dataKey: ObjectPropertyPath<TResource>;
    payload: TResource;
}
type PayloadArray<TResource> = Array<TypedPayload<TResource>>;
type RechartsTooltipProps = TooltipContentProps<ValueType, NameType>;
interface RenderProps<TResource> extends Partial<Omit<RechartsTooltipProps, "payload">> {
    payload: PayloadArray<TResource>;
    label?: string | number;
}
interface ChartTooltipProps<TResource> extends Partial<RechartsTooltipProps> {
    children: (renderProps: RenderProps<TResource>) => React.ReactElement;
}
export function ChartTooltip<TResource>({ children, ...props }: ChartTooltipProps<TResource>) {
    if (props.active && props.payload && props.payload.length) {
        const { payload, ...rest } = props;
        const payloadArray: PayloadArray<TResource> = payload
            .filter((p) => p.payload !== undefined)
            .map((p) => {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const path = p.dataKey as ObjectPropertyPath<TResource>;
            return {
                ...p,
                payload: p.payload,
                dataKey: path,
            };
        });
        return <div className={chartTooltipStyles}>{children({ ...rest, payload: payloadArray })}</div>;
    }
    return null;
}
const chartTooltipStyles = css({
    padding: space[16],
    backgroundColor: "rgba(0,0,0,0.7)",
    borderRadius: borderRadius.medium,
});
